/* Shopify cart transport. A request token identifies an attempt; it is not server idempotency. */
(() => {
  'use strict';
  function reconcile(cart, attempt) {
    if (!cart || !Array.isArray(cart.items)) return 'unknown';
    const actual = new Map();
    for (const line of cart.items) if (line.properties?._tidy_attempt === attempt.token) {
      const key = `${line.variant_id}:${line.properties?._tidy_part}`;
      actual.set(key, (actual.get(key) || 0) + line.quantity);
    }
    if (!actual.size) return 'absent';
    const expected = new Map(attempt.items.map(i => [`${i.id}:${i.properties._tidy_part}`, i.quantity]));
    return actual.size === expected.size && [...expected].every(([key, qty]) => actual.get(key) === qty) ? 'complete' : 'partial';
  }
  function buildItems(model, token) {
    if (!model.rows.length || model.pending || model.incomplete || model.rows.some(r => r.unavailable)) throw new Error('Choose available products with confirmed SKUs.');
    return model.rows.map(row => ({ id: String(row.variant.id), quantity: row.quantity,
      properties: { 'Tidy Trim piece': row.part.title, _tidy_part: row.part.key, _tidy_attempt: token } }));
  }
  class CartTransport {
    constructor({ fetcher, root = '/', save }) { this.fetcher = fetcher; this.root = root; this.save = save; this.busy = false; }
    async request(path, options = {}) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 20000);
      try { return await this.fetcher(`${this.root}${path}`, { credentials: 'same-origin', cache: 'no-store', ...options, signal: controller.signal }); }
      finally { clearTimeout(timer); }
    }
    async inspect(attempt) {
      try {
        const response = await this.request('cart.js');
        if (!response.ok) throw new Error('Cart unavailable');
        const cart = await response.json();
        return { state: reconcile(cart, attempt), cart };
      } catch (_) { return { state: 'unknown' }; }
    }
    async add(attempt, sections, sectionsUrl) {
      if (this.busy) throw new Error('A cart request is already in progress.');
      this.busy = true;
      let response, payload;
      try {
        // Persist BEFORE sending. Reload/Back must never silently repeat a possibly committed POST.
        this.save({ ...attempt, state: 'sending' });
        try {
          response = await this.request('cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: attempt.items, sections, sections_url: sectionsUrl }) });
          payload = await response.json();
        } catch (_) { /* Reconcile network errors, timeouts and invalid JSON without replaying POST. */ }
        const result = await this.inspect(attempt);
        // Even a 422 can partially add stock. An absent read after a timeout is not proof of failure.
        if (result.state === 'absent') result.state = response && !response.ok && response.status < 500 ? 'rejected' : 'unknown';
        this.save({ ...attempt, state: result.state });
        return { ...result, payload, message: typeof payload?.description === 'string' ? payload.description : '' };
      } finally { this.busy = false; }
    }
  }
  const api = { reconcile, buildItems, CartTransport };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else window.TidyTrimCart = api;
})();
